import type { VisitRow } from "./database.types";

// Default visit length (minutes) when we don't have an explicit duration on
// the visit row. 30 is the typical async-or-sync video-visit slot length.
const DEFAULT_VISIT_MINUTES = 30;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

// Format a Date as a UTC ICS / Google Calendar timestamp: YYYYMMDDTHHMMSSZ
function utcStamp(d: Date): string {
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

function escapeIcs(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function visitTitle(visit: VisitRow): string {
  const provider = visit.provider?.full_name;
  return provider ? `ForteHealth visit with ${provider}` : "ForteHealth visit";
}

function visitStart(visit: VisitRow): Date | null {
  if (!visit.scheduled_at) return null;
  const d = new Date(visit.scheduled_at);
  return Number.isNaN(d.getTime()) ? null : d;
}

function visitEnd(start: Date): Date {
  return new Date(start.getTime() + DEFAULT_VISIT_MINUTES * 60 * 1000);
}

function visitDescription(visit: VisitRow): string {
  const lines: string[] = ["Video visit with your ForteHealth provider."];
  if (visit.video_link) {
    lines.push("", `Join link: ${visit.video_link}`);
  }
  lines.push("", "Manage this visit in your patient portal: https://fortehealth-portal.pages.dev/visits");
  return lines.join("\n");
}

// Returns a Google Calendar "create event" URL with the visit prefilled.
// Opens in a new tab; user reviews + saves.
export function googleCalendarUrl(visit: VisitRow): string | null {
  const start = visitStart(visit);
  if (!start) return null;
  const end = visitEnd(start);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: visitTitle(visit),
    dates: `${utcStamp(start)}/${utcStamp(end)}`,
    details: visitDescription(visit),
  });
  if (visit.video_link) params.set("location", visit.video_link);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Generates an .ics file body for the visit (RFC 5545). Used for Apple
// Calendar / Outlook / generic calendar imports.
function icsContent(visit: VisitRow): string | null {
  const start = visitStart(visit);
  if (!start) return null;
  const end = visitEnd(start);
  const now = new Date();
  const uid = `${visit.id}@fortehealth.co`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ForteHealth//Patient Portal//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${utcStamp(now)}`,
    `DTSTART:${utcStamp(start)}`,
    `DTEND:${utcStamp(end)}`,
    `SUMMARY:${escapeIcs(visitTitle(visit))}`,
    `DESCRIPTION:${escapeIcs(visitDescription(visit))}`,
  ];
  if (visit.video_link) {
    lines.push(`LOCATION:${escapeIcs(visit.video_link)}`);
    lines.push(`URL:${visit.video_link}`);
  }
  lines.push(
    "STATUS:CONFIRMED",
    "BEGIN:VALARM",
    "TRIGGER:-PT30M",
    "ACTION:DISPLAY",
    "DESCRIPTION:Your ForteHealth visit starts in 30 minutes",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  );

  // CRLF line endings per RFC 5545.
  return lines.join("\r\n");
}

// Triggers a browser download of the .ics file for the given visit.
export function downloadIcs(visit: VisitRow): void {
  const body = icsContent(visit);
  if (!body) return;
  const blob = new Blob([body], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fortehealth-visit-${visit.id.slice(0, 8)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
